import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	getUserFacingErrorMessage,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { DocumentSigningRoleType, SignWetInkDocumentDto } from '@dealstack/shared/lib/generated';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { cleanFileName } from '@dealstack/shared/lib/common/file/file';
import { useSignWetInkDocumentUploadUrl } from '@dealstack/frontend/shared/modules/document/signing/hooks/useSignWetInkDocumentUploadUrl';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getDocumentWorkflowEventsQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';
import { calculateMD5ForFile } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/utils/calculateMD5ForFile';

const signWetInkSignDocumentWorkflow = async ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	signingRoleType,
	body,
}: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	body: SignWetInkDocumentDto;
	signingRoleType: string;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_SIGN_WET_INK_SIGNINGROLETYPE_POST,
			{ dealId, programmeId, documentWorkflowInstanceId, signingRoleType }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
};

export const useSignWetInkDocument = (options: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
}) => {
	const tMipManage = useTranslationMipManage();
	const { dealId, programmeId, documentWorkflowInstanceId } = options;
	const queryClient = useQueryClient();
	const signWetInkDocumentUploadUrl = useSignWetInkDocumentUploadUrl({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
	});

	const wetInkSignDocumentWorkflowMutation = useMutation({
		mutationFn: signWetInkSignDocumentWorkflow,

		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({
					queryKey: [
						formatUnicorn(
							API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_GET,
							{ dealId, programmeId, documentWorkflowInstanceId }
						),
					],
				}),
				queryClient.invalidateQueries({
					queryKey: getDocumentWorkflowEventsQueryKey({
						programmeId,
						documentWorkflowInstanceId,
					}),
				}),
			]);
		},
	});

	// hook used to handle the entire wet ink signing workflow
	// 1. Get pre-signed upload URL
	// 2. Upload file to pre-signed URL
	// 3. Call sign wet ink document workflow API
	const handleSigningDocumentWorkflow = async ({
		selectedFile,
		selectedRoleName,
		signingRoleType,
		onSuccess,
		investorId,
	}: {
		selectedFile: File;
		selectedRoleName?: string;
		signingRoleType: DocumentSigningRoleType;
		onSuccess: () => void;
		investorId?: string;
	}) => {
		const uploadFileData = {
			extension: DocumentService.getFileExtensionFromName(selectedFile.name),
			mimeType: selectedFile.type,
			fileName: cleanFileName(selectedFile.name),
		};

		const preSignedUrlData = await signWetInkDocumentUploadUrl.mutateAsync({
			signingRoleType,
			roleName: selectedRoleName,
			fileData: uploadFileData,
			investorId,
		});
		if (!preSignedUrlData) {
			showToastV2({
				title: tMipManage('documents_page.toast.error.upload_url_not_available'),
				state: 'error',
			});
			return;
		}

		const md5 = await DocumentService.putUploadToPreSignedUrl({
			preSignedUrl: preSignedUrlData.uploadPreSignedUrl,
			file: selectedFile,
		});

		const fileMd5 = await calculateMD5ForFile(selectedFile, 'error calculating file MD5');

		if (md5 !== `"${fileMd5}"`) {
			showToastV2({
				title: tMipManage('documents_page.toast.error.file_upload_mismatch'),
				state: 'error',
			});
			return;
		}

		const fileData = {
			md5: fileMd5,
			fileKey: preSignedUrlData.fileKey,
			fileExtension: uploadFileData.extension,
			mimeType: uploadFileData.mimeType,
			fileName: uploadFileData.fileName,
		};

		return await wetInkSignDocumentWorkflowMutation.mutateAsync(
			{
				dealId,
				programmeId,
				documentWorkflowInstanceId,
				signingRoleType,
				body: {
					fileData,
					roleName: selectedRoleName,
					investorId,
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tMipManage('documents_page.toast.success.document_signed'),
						state: 'success',
					});
					onSuccess();
				},
				onError: (error: Error) => {
					showToastV2({
						title:
							getUserFacingErrorMessage(error) ??
							tMipManage('documents_page.toast.error.failed_to_sign_document'),
						state: 'error',
					});
				},
			}
		);
	};

	return {
		handleSigningDocumentWorkflow,
		wetInkSignDocumentWorkflowIsLoading: wetInkSignDocumentWorkflowMutation.isPending,
	};
};
